import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from 'react-apollo';

import useAuthenticated from './useAuthenticated';
import { AlbumFolderSubscriptionPromoCodes } from '../graphql/queries/album-folder';
import { AccountPromoCodeVerify } from '../graphql/queries/account-referrals';
import { ACCOUNT_REFERRAL_TYPE } from '../constants/account-referrals';
import { pickFreeSubscriptionPromoCode } from '../util/promo-codes';
import { sentryCaptureMessage } from '../util/sentry';

/**
 * Finds the event's promo code that makes a Digital Photo Pass free and
 * verifies the current user can use it.
 *
 * Only runs for authenticated users: `accountPromoCodeVerify` reports every
 * anonymous viewer as eligible, so an unauthenticated result is meaningless.
 *
 * @param {Object} opts The options.
 * @param {string} opts.albumFolderId The event (root album folder) id.
 * @param {boolean} [opts.enabled] Whether a pass is on offer / in the cart.
 * @returns {{ promoCode: Object|null, loading: boolean }}
 */
const useFreeSubscriptionPromoCode = ({ albumFolderId, enabled = true }) => {
  const authenticated = useAuthenticated();
  const client = useApolloClient();
  const active = !!albumFolderId && enabled && authenticated;

  const { data: { albumFolder } = {}, loading: loadingPromoCodes } = useQuery(
    AlbumFolderSubscriptionPromoCodes,
    {
      variables: { id: albumFolderId },
      skip: !active,
      fetchPolicy: 'cache-first',
      onError: error => {
        sentryCaptureMessage(
          '[useFreeSubscriptionPromoCode] :: Could not load promo codes',
          { level: 'warning', extra: { error, albumFolderId } },
        );
      },
    },
  );

  const candidate = useMemo(() => {
    if (!active) {
      return null;
    }

    return pickFreeSubscriptionPromoCode(
      albumFolder?.subscription_promo_codes?.records ?? [],
    );
  }, [active, albumFolder]);

  const [verification, setVerification] = useState({
    code: null,
    promoCode: null,
    loading: false,
  });

  useEffect(() => {
    if (!candidate) {
      return undefined;
    }

    let cancelled = false;

    setVerification({ code: candidate.code, promoCode: null, loading: true });

    client
      .query({
        query: AccountPromoCodeVerify,
        variables: { promoCode: candidate.code },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const result = data?.accountPromoCodeVerify;
        const usable =
          !!result?.is_valid &&
          !!result?.is_eligible &&
          result.type === ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS;

        if (!cancelled) {
          setVerification({
            code: candidate.code,
            promoCode: usable ? candidate : null,
            loading: false,
          });
        }
      })
      .catch(error => {
        sentryCaptureMessage(
          '[useFreeSubscriptionPromoCode] :: Could not verify promo code',
          { level: 'warning', extra: { error, albumFolderId } },
        );

        if (!cancelled) {
          setVerification({
            code: candidate.code,
            promoCode: null,
            loading: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [candidate?.code, authenticated]);

  const verified =
    !!candidate && verification.code === candidate.code
      ? verification
      : { promoCode: null, loading: !!candidate };

  return {
    promoCode: verified.promoCode,
    loading: active && (loadingPromoCodes || verified.loading),
  };
};

export default useFreeSubscriptionPromoCode;
